import type { CombinedError } from "urql"
import { useMutation } from "urql"

import type {
  CreateWarehouseMutation,
  WarehouseAreaCreateInput,
  WarehouseAreaMutationCreateArgs,
} from "@/graphql/codegen/graphql"
import { CreateWarehouseDocument } from "@/graphql/codegen/graphql"
import { NotificationType } from "@/schemas/notification"
import { useNotificationsStore } from "@/stores/useNotificationsStore"

interface UseCreateWarehouseProps {
  onSuccess: () => void
}

interface UseCreateWarehouseReturn {
  create: (input: WarehouseAreaCreateInput) => Promise<void>
  isCreating: boolean
  error?: CombinedError
}

export const useCreateWarehouse = ({ onSuccess }: UseCreateWarehouseProps): UseCreateWarehouseReturn => {
  const notify = useNotificationsStore((state) => state.enqueueNotification)
  const [{ fetching, error }, executeMutation] = useMutation<CreateWarehouseMutation, WarehouseAreaMutationCreateArgs>(
    CreateWarehouseDocument
  )

  const create = async (input: WarehouseAreaCreateInput) => {
    const { data, error } = await executeMutation({ input })

    const hasError = error && data?.warehouseArea.create.__typename === "WarehouseAreaCreateFailure"

    if (hasError) {
      notify({
        type: NotificationType.Error,
        message: error?.message ?? "Failed to create warehouse.",
      })
      return
    }

    notify({
      type: NotificationType.Success,
      content: "Warehouse successfully created.",
    })
    onSuccess()
  }

  return {
    create,
    isCreating: fetching,
    error,
  }
}
